/**
 * The ward list endpoint, as the frontend sees it.
 *
 * Mirrors `api/src/Continuum.Api/Ward/WardListEntry.cs`. Values and codes, never
 * rendered text: every date here is an instant or a calendar date and goes through
 * a shared formatter on the way to the screen (I18N.md §3), and every status is a
 * code the catalogue turns into Swedish (AGENTS.md §7).
 */

import { unauthenticated } from '../auth/sessionExpiry'
import type {
  AllergyStatus,
  IdentifierSystem,
  PatientBirthDate,
  PatientIdentifier,
  PatientNames,
  PatientSafety,
} from '../patient/patientRecord'

/**
 * One ward in the picker. Mirrors `Ward/WardDirectoryQuery.cs`'s `WardSummary`.
 *
 * `ward` is the code — `M3` — because that is what a clinician knows and what
 * `/api/wards/{ward}/patients` takes. The ward's uuid is deliberately not served: nobody
 * on a ward knows it, and a screen that held it would be a screen that could send it on.
 *
 * No occupancy, and that is the server's decision rather than something the picker chose
 * not to render — see `WardDirectory` for why a `24/30` here would turn a list of ward
 * names into patient data.
 */
export interface WardSummary {
  readonly ward: string
  readonly name: string
  readonly bedCount: number | null
}

export interface WardDirectory {
  readonly wards: readonly WardSummary[]
}

/**
 * A ward-list row: a patient, plus what is true of their admission.
 *
 * The patient half comes from `patient/patientRecord.ts` and is shared with the
 * search — the two screens are the two ways into a record (PHASE-0.md §3.4), and one
 * declaration is what stops them drifting into two ideas of what an allergy status
 * is. What is declared here is the admission: the bed, when it started, and who is
 * responsible.
 */
export interface WardListEntry
  extends PatientNames,
    PatientIdentifier,
    PatientBirthDate,
    PatientSafety {
  /** Null for a patient on the ward with no bed — a corridor, or before one was assigned. */
  readonly bed: string | null
  readonly patientId: string

  readonly identifierSystem: IdentifierSystem | null

  /** ISO 8601 instant. */
  readonly admittedAt: string

  readonly responsibleGivenName: string | null
  readonly responsibleFamilyName: string | null

  readonly allergyStatus: AllergyStatus
}

export interface WardList {
  readonly ward: string
  readonly name: string
  readonly bedCount: number | null
  readonly entries: readonly WardListEntry[]
}

/** Why a fetch did not produce a list. Codes, so the catalogue does the words. */
export type WardListFailure = 'not-found' | 'unavailable'

export class WardListError extends Error {
  readonly failure: WardListFailure

  constructor(failure: WardListFailure) {
    super(`Ward list unavailable: ${failure}`)
    this.name = 'WardListError'
    this.failure = failure
  }
}

/**
 * Fetches the wards a clinician may pick between.
 *
 * This replaced a `PHASE_0_WARD = 'M3'` constant compiled into the bundle. The constant was
 * honest while `--seed-demo` created exactly one ward, and it is worth saying what changed:
 * not the frontend's need, but the record's — a second ward row makes a hard-coded code a
 * screen that cannot reach half the patients on the box.
 *
 * No 404 case, unlike the list below: an empty directory is a real answer. A cluster that has
 * been created but not seeded has no wards, and saying so is better than claiming the
 * directory itself is missing.
 */
export async function fetchWardDirectory(signal: AbortSignal): Promise<WardDirectory> {
  let response: Response

  try {
    response = await fetch('/api/wards', { signal, headers: { Accept: 'application/json' } })
  } catch (cause) {
    if (signal.aborted) {
      throw cause
    }
    throw new WardListError('unavailable')
  }

  unauthenticated(response)

  if (!response.ok) {
    throw new WardListError('unavailable')
  }

  return (await response.json()) as WardDirectory
}

/**
 * Fetches the ward list.
 *
 * A 404 is a different answer from a failure and is kept separate all the way to
 * the screen: "no such ward" tells a clinician to check the address on the printed
 * sheet, "unavailable" tells them to call the technician.
 */
export async function fetchWardList(ward: string, signal: AbortSignal): Promise<WardList> {
  let response: Response

  try {
    response = await fetch(`/api/wards/${encodeURIComponent(ward)}/patients`, {
      signal,
      headers: { Accept: 'application/json' },
    })
  } catch (cause) {
    // An aborted fetch is a navigation, not a failure. Re-thrown so the caller can
    // tell the two apart rather than rendering an error for a screen nobody is
    // looking at any more.
    if (signal.aborted) {
      throw cause
    }
    throw new WardListError('unavailable')
  }

  if (response.status === 404) {
    throw new WardListError('not-found')
  }

  // Before the general case, because a 401 is not an unavailable box: it means the session
  // has ended, and `SessionGate` is about to show the login screen. Reported rather than
  // rendered — see `auth/sessionExpiry.ts`. The throw still happens, so this screen does not
  // sit half-updated in the moment before it unmounts.
  unauthenticated(response)

  if (!response.ok) {
    throw new WardListError('unavailable')
  }

  return (await response.json()) as WardList
}
